import { Box, Typography } from '@mui/material'
import React from 'react'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import {
  BetmenList,
  BetmenListHeader,
  BetmenListHeaderCell,
  BetmenListBody,
  BetmenListItemCard,
  BetmenListItemCardCell,
} from '../../../../../common/components'
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation'
import dayjs from 'dayjs'
import { BetmenListItemCardProps } from '../../../../../common/components/BetmenList/BetmenListItemCard'
import { CaseGroup, CaseStatus } from '../../../../../models/graphqlTypes'
import { UsePatientEProfileReturn } from '../../hooks/usePatientEProfile'
import { PatientDetailsSectionHeader } from './PatientDetailsSectionHeader'
import { PatientDetailsActions } from './PatientDetailsActions'

interface TreatmentCardProps extends BetmenListItemCardProps {
  treatment: CaseGroup
  isEditable: boolean
}

const TreatmentCard: React.FC<TreatmentCardProps> = ({
  treatment,
  isEditable,
  ...props
}) => {
  const { t } = useTranslation()

  return (
    <BetmenListItemCard key={treatment.id} {...props}>
      <BetmenListItemCardCell typographyProps={{ fontWeight: 700 }}>
        {treatment.title}
      </BetmenListItemCardCell>
      <BetmenListItemCardCell typographyProps={{ fontWeight: 400 }}>
        {dayjs(treatment.createdAt).format('YYYY. MMMM')}
      </BetmenListItemCardCell>
      <BetmenListItemCardCell>
        {t(
          `doctor:patientProfile.treatments.status.${treatment.status.toLowerCase()}`
        )}
      </BetmenListItemCardCell>
      <BetmenListItemCardCell typographyProps={{ fontWeight: 400 }}>
        {dayjs(treatment.updatedAt).format('YYYY. MMMM')}
      </BetmenListItemCardCell>

      <PatientDetailsActions isEditable={isEditable} />
    </BetmenListItemCard>
  )
}

export const PatientTreatments: React.FC<UsePatientEProfileReturn> = ({
  patientDetails,
  isLoading,
  isEditable,
}) => {
  const { t } = useTranslation()

  const futureCases = patientDetails.cases.filter(
    (treatment) =>
      treatment.status === CaseStatus.Active ||
      treatment.status === CaseStatus.Inactive
  )
  const pastCases = patientDetails.cases.filter(
    (treatment) =>
      treatment.status === CaseStatus.Closed ||
      treatment.status === CaseStatus.Solved
  )

  const hasData = patientDetails.cases.length > 0

  return (
    <Box bgcolor={'pink.100'} py={2} borderRadius={3}>
      <PatientDetailsSectionHeader
        icon={<MedicalInformationIcon fontSize="large" />}
        title={t('doctor:patientProfile.treatments.title')}
        hasData={hasData}
      />

      {(isLoading || hasData) && (
        <BetmenList
          dataSource={futureCases}
          loading={isLoading}
          gridColumnSizes={[5, 2, 2, 2, 1]}
          extraHeaderStyle={{ backgroundColor: 'transparent' }}
          stickyHeader={false}
          header={
            <BetmenListHeader>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.treatments.header.title')}
              </BetmenListHeaderCell>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.treatments.header.startDate')}
              </BetmenListHeaderCell>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.treatments.header.status')}
              </BetmenListHeaderCell>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.treatments.header.lastStatusChange')}
              </BetmenListHeaderCell>
            </BetmenListHeader>
          }
        >
          <BetmenListBody>
            <tr>
              <td>
                <Typography variant="body2" px={1} pb={2}>
                  {t('doctor:patientProfile.treatments.actual')}
                </Typography>
              </td>
            </tr>
            {futureCases.length > 0 &&
              futureCases.map((treatment) => (
                <TreatmentCard
                  key={treatment.id}
                  treatment={treatment}
                  isEditable={isEditable}
                />
              ))}

            <tr>
              <td>
                <Typography variant="body2" px={1} pb={2}>
                  {t('doctor:patientProfile.treatments.closed')}
                </Typography>
              </td>
            </tr>
            {pastCases.length > 0 &&
              pastCases.map((treatment) => (
                <TreatmentCard
                  key={treatment.id}
                  treatment={treatment}
                  color="grey.600"
                  sx={{
                    bgcolor: 'grey.200',
                  }}
                  isEditable={isEditable}
                />
              ))}
          </BetmenListBody>
        </BetmenList>
      )}
    </Box>
  )
}
